import { Box, Container, Typography, Button, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const previewImages = [
  { src: '/images/gallery/wall-cleaning.jpeg', title: 'Wall Cleaning' },
  { src: '/images/gallery/balcony-cleaning.jpeg', title: 'Balcony Cleaning' },
  { src: '/images/gallery/team-uniforms.jpeg', title: 'Our Team' },
  { src: '/images/gallery/balcony-view.jpeg', title: 'High-Rise Cleaning' },
  { src: '/images/gallery/team-floor.jpeg', title: 'Floor Cleaning' },
  { src: '/images/gallery/building-cleaning.jpeg', title: 'Building Cleaning' },
];

const GalleryPreview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            align="center"
            sx={{
              mb: 1,
              fontWeight: 800,
              background: 'linear-gradient(45deg, #22335F, #366495)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            See Our Work
          </Typography>
          <Typography
            variant="body1"
            align="center"
            color="text.secondary"
            sx={{ mb: 5, maxWidth: 550, mx: 'auto' }}
          >
            Real results from our professional cleaning team across Kampala
          </Typography>
        </motion.div>

        {/* Masonry-style grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gridAutoRows: { xs: 180, md: 220 },
            gap: 2,
            mb: 5,
          }}
        >
          {previewImages.map((img, index) => (
            <motion.div
              key={img.src}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.03 }}
              style={{
                gridRow: index === 0 || index === 3 ? 'span 2' : 'span 1',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/gallery')}
            >
              <Box
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  '&:hover .overlay': { opacity: 1 },
                }}
              >
                <img
                  src={img.src}
                  alt={img.title}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Box
                  className="overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(transparent 40%, rgba(34,51,95,0.8))',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    display: 'flex',
                    alignItems: 'flex-end',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: 'white', fontWeight: 600 }}
                  >
                    {img.title}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/gallery')}
              sx={{
                backgroundColor: '#49914F',
                px: 5,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#3d7a43' },
              }}
            >
              View Full Gallery
            </Button>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default GalleryPreview;
